import styled from '@emotion/styled';
import { useCallback } from 'react';
import { Stack, Text } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { IconOption } from '../../../../common/AdvancedSelectA11y/IconOption';
import { AdvancedSelect } from '../../../../common/AdvancedSelectA11y/AdvancedSelect';
import type {
  NetPromoterScoreQuestion,
  RatingScale5Question,
  RatingScale7Question,
  SingleCloseEndedQuestion,
  SurveyQuestion,
  ReactionQuestion,
} from '../../../../../domain/Surveys/Questions/SurveyQuestion';
import { getQuestionLabelAndIcon } from '../../../utils';
import type { BasedOnAnswer, BaseQuestionOption, NextQuestionOption, AnswerLabel } from './common';
import { useQuestionLabel } from './common';

const OptionContainer = styled(Stack)`
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

function getUpdatedBasedOnByIndex(
  current: BasedOnAnswer,
  index: number,
  option: BaseQuestionOption,
): BasedOnAnswer {
  return {
    type: 'based-on-answer',
    options: current.options.map((existing, i) => (i === index ? option : existing)),
  };
}

const TRANSLATION_PREFIX = 'feedback:wizard.section.questions.next-question';

export const BasedOnAnswerSelector = ({
  basedOnAnswer,
  question,
  questions,
  onChange,
  label,
  index,
}: {
  basedOnAnswer: BasedOnAnswer;
  question:
    | RatingScale5Question
    | RatingScale7Question
    | NetPromoterScoreQuestion
    | SingleCloseEndedQuestion
    | ReactionQuestion;
  questions: SurveyQuestion[];
  onChange: (option: NextQuestionOption) => void;
  label: AnswerLabel;
  index: number;
}): JSX.Element => {
  const option = basedOnAnswer.options[index];
  const { t } = useTranslationWithPrefix(TRANSLATION_PREFIX);
  const getQuestionLabel = useQuestionLabel();

  const onSelect = useCallback(
    (value: 'by-order' | 'thank-you' | string) => {
      if (value === 'by-order') {
        onChange(getUpdatedBasedOnByIndex(basedOnAnswer, index, { type: 'by-order' }));
        return;
      }

      if (value === 'thank-you') {
        onChange(getUpdatedBasedOnByIndex(basedOnAnswer, index, { type: 'thank-you' }));
        return;
      }

      const selectedQuestion = questions.find((q) => q.uuid === value);
      if (selectedQuestion === undefined) return;
      onChange(
        getUpdatedBasedOnByIndex(basedOnAnswer, index, {
          type: 'survey-question',
          question: selectedQuestion,
        }),
      );
    },
    [basedOnAnswer, index, onChange, questions],
  );

  return (
    <OptionContainer direction="row" key={question.uuid}>
      <Text>
        <Text textType="subtitle2">{label.content ?? label.text}</Text>
        <Text>{`, ${t('go-to')}:`}</Text>
      </Text>
      <AdvancedSelect
        id="based-on-answer-selector"
        ariaLabel={t('next-if', { labelText: label.text })}
        onSelect={onSelect}
        selectedValue={option.type === 'survey-question' ? option.question.uuid : option.type}
        maxWidth={200}
      >
        {[
          <IconOption id="by-order" key="by-order" value="by-order">
            {getQuestionLabel(questions, { type: 'by-order' })}
          </IconOption>,
          <IconOption id="thank-you" key="thank-you" value="thank-you">
            {getQuestionLabel(questions, { type: 'thank-you' })}
          </IconOption>,
          ...questions
            .filter((q) => q.uuid !== question.uuid)
            .map((questionOption) => (
              <IconOption
                key={questionOption.uuid}
                id={questionOption.uuid}
                icon={getQuestionLabelAndIcon(questionOption.type).icon}
                value={questionOption.uuid}
              >
                {getQuestionLabel(questions, {
                  type: 'survey-question',
                  question: questionOption,
                })}
              </IconOption>
            )),
        ]}
      </AdvancedSelect>
    </OptionContainer>
  );
};
